import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetInvitations,
  useRevokeInvitation,
  useCreateInvitation,
  useGetMembers,
  useRemoveMember,
  useUpdateMemberRole,
} from '../../hooks/useTeam';
import { useAuthStore } from '../../store/authStore';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import { EmptyState } from '../../components/ui/EmptyState';
import { InvitationForm } from '../../components/forms/InvitationForm';
import { format } from 'date-fns';
import type { CreateInvitationData, TeamInvitation, TeamMember } from '../../types/team';

export const TeamPage: React.FC = () => {
  const { t } = useTranslation('teams');
  const { user } = useAuthStore();
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [revokeModalOpen, setRevokeModalOpen] = useState(false);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState<TeamInvitation | null>(null);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);

  const { data: invitations, isLoading: invitationsLoading, error: invitationsError } = useGetInvitations();
  const { data: members, isLoading: membersLoading, error: membersError } = useGetMembers();
  const revokeInvitation = useRevokeInvitation();
  const createInvitation = useCreateInvitation();
  const removeMember = useRemoveMember();
  const updateMemberRole = useUpdateMemberRole();

  // Check if user is owner
  const isOwner = user?.account?.owner_user_id === user?.id;

  const handleSendInvitation = (data: CreateInvitationData) => {
    createInvitation.mutate(data, {
      onSuccess: () => {
        setSendModalOpen(false);
      },
    });
  };

  const handleRevokeInvitation = () => {
    if (!selectedInvitation) return;

    revokeInvitation.mutate(selectedInvitation.id, {
      onSuccess: () => {
        setRevokeModalOpen(false);
        setSelectedInvitation(null);
      },
    });
  };

  const handleRemoveMember = () => {
    if (!selectedMember) return;

    removeMember.mutate(selectedMember.id, {
      onSuccess: () => {
        setRemoveModalOpen(false);
        setSelectedMember(null);
      },
    });
  };

  const handleRoleChange = (memberId: number, newRole: 'manager') => {
    updateMemberRole.mutate({ memberId, data: { role: newRole } });
  };

  const openRevokeModal = (invitation: TeamInvitation) => {
    setSelectedInvitation(invitation);
    setRevokeModalOpen(true);
  };

  const openRemoveModal = (member: TeamMember) => {
    setSelectedMember(member);
    setRemoveModalOpen(true);
  };

  // Check expiry status
  const getExpiryStatus = (expiresAt: string) => {
    const expiry = new Date(expiresAt);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return { status: 'expired', variant: 'error' as const, text: t('invitations.expired') };
    } else if (daysUntilExpiry <= 1) {
      return { status: 'expiring-soon', variant: 'warning' as const, text: t('invitations.expires_soon') };
    } else {
      return {
        status: 'active',
        variant: 'success' as const,
        text: t('invitations.days_left', { days: daysUntilExpiry }),
      };
    }
  };

  // Access control: only owners can view this page
  if (!isOwner) {
    return (
      <div className="p-8">
        <Alert variant="error" title={t('page.unauthorized_title')}>
          {t('page.unauthorized_message')}
        </Alert>
      </div>
    );
  }

  // Loading state
  if (membersLoading || invitationsLoading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <Spinner size="large" />
          <span className="ml-3 text-gray-600">{t('page.loading')}</span>
        </div>
      </div>
    );
  }

  // Error state
  if (membersError || invitationsError) {
    return (
      <div className="p-8">
        <Alert variant="error" title={t('page.error_title')}>
          {membersError instanceof Error ? membersError.message : t('page.error_message')}
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('page.title')}</h1>
          <p className="text-gray-600 mt-1">{t('page.subtitle')}</p>
        </div>
        <Button onClick={() => setSendModalOpen(true)}>{t('page.invite_button')}</Button>
      </div>

      {/* Team Members Card */}
      <Card>
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-4">{t('members.section_title')}</h2>

          {members && members.length === 0 ? (
            <EmptyState
              title={t('members.no_members_title')}
              description={t('members.no_members_description')}
              action={
                <Button onClick={() => setSendModalOpen(true)}>{t('page.invite_button')}</Button>
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('members.table.name')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('members.table.email')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('members.table.role')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('members.table.joined')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('members.table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {members?.map((member) => (
                    <tr key={member.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-gray-900">{member.name}</span>
                          {member.id === user?.id && (
                            <Badge variant="default" className="ml-2">
                              {t('members.you_badge')}
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {member.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {member.role === 'owner' ? (
                          <Badge variant="success">{t('members.role_owner')}</Badge>
                        ) : (
                          <select
                            value={member.role}
                            onChange={(e) => handleRoleChange(member.id, e.target.value as 'manager')}
                            className="text-sm border border-gray-300 rounded-md px-2 py-1"
                            disabled={updateMemberRole.isPending}
                          >
                            <option value="manager">{t('members.role_manager')}</option>
                          </select>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {format(new Date(member.joined_at), 'MMM d, yyyy')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {member.role !== 'owner' && member.id !== user?.id && (
                          <Button
                            variant="danger"
                            className="text-xs py-1 px-3"
                            onClick={() => openRemoveModal(member)}
                          >
                            {t('members.remove_button')}
                          </Button>
                        )}
                        {member.role === 'owner' && (
                          <span className="text-xs text-gray-400">{t('members.cannot_remove_owner')}</span>
                        )}
                        {member.id === user?.id && member.role !== 'owner' && (
                          <span className="text-xs text-gray-400">{t('members.cannot_remove_yourself')}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {/* Pending Invitations Card */}
      <Card>
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-4">{t('invitations.section_title')}</h2>

          {invitations && invitations.length === 0 ? (
            <EmptyState
              title={t('invitations.no_invitations_title')}
              description={t('invitations.no_invitations_description')}
              action={
                <Button onClick={() => setSendModalOpen(true)}>{t('invitations.send_button')}</Button>
              }
            />
          ) : (
            <div data-testid="invitations-table" className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('invitations.table.email')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('invitations.table.role')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('invitations.table.invited_by')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('invitations.table.expires')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('invitations.table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invitations?.map((invitation) => {
                    const expiryStatus = getExpiryStatus(invitation.expires_at);
                    return (
                      <tr key={invitation.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {invitation.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge variant="default">
                            {invitation.role.charAt(0).toUpperCase() + invitation.role.slice(1)}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {invitation.invited_by || t('invitations.unknown')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col">
                            <span className="text-sm text-gray-900">
                              {new Date(invitation.expires_at).toLocaleDateString()}
                            </span>
                            <Badge variant={expiryStatus.variant} className="mt-1 inline-block w-fit">
                              {expiryStatus.text}
                            </Badge>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <Button
                            variant="danger"
                            className="text-xs py-1 px-3"
                            onClick={() => openRevokeModal(invitation)}
                          >
                            {t('invitations.revoke_button')}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {/* Send Invitation Modal */}
      <Modal
        open={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        title={t('invite_modal.title')}
      >
        <InvitationForm
          onSubmit={handleSendInvitation}
          onCancel={() => setSendModalOpen(false)}
          isSubmitting={createInvitation.isPending}
        />
      </Modal>

      {/* Revoke Invitation Modal */}
      <Modal
        open={revokeModalOpen}
        onClose={() => setRevokeModalOpen(false)}
        title={t('revoke_modal.title')}
      >
        <div className="space-y-4">
          <Alert variant="warning">
            {t('revoke_modal.warning')}
          </Alert>
          <p className="text-gray-700">
            {t('revoke_modal.confirm', { email: selectedInvitation?.email })}
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setRevokeModalOpen(false)}
              disabled={revokeInvitation.isPending}
            >
              {t('revoke_modal.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={handleRevokeInvitation}
              loading={revokeInvitation.isPending}
            >
              {t('revoke_modal.revoke')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Remove Member Modal */}
      <Modal
        open={removeModalOpen}
        onClose={() => setRemoveModalOpen(false)}
        title={t('remove_modal.title')}
      >
        <div className="space-y-4">
          <Alert variant="warning">
            {t('remove_modal.warning')}
          </Alert>
          <p className="text-gray-700">
            {t('remove_modal.confirm', { name: selectedMember?.name, email: selectedMember?.email })}
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setRemoveModalOpen(false)}
              disabled={removeMember.isPending}
            >
              {t('remove_modal.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={handleRemoveMember}
              loading={removeMember.isPending}
            >
              {t('remove_modal.remove')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TeamPage;
